import {
  FileText,
  History,
  KeyRound,
  Leaf,
  Lock,
  Mail,
  Network,
  ShieldCheck,
  Smartphone,
  Truck,
  Wallet,
  Warehouse,
  type LucideIcon,
} from 'lucide-react';
import { Link } from 'react-router';
import { COMPANY, isPlaceholder } from './company';
import { PublicLayout, SignInButton } from './PublicLayout';

interface SuiteApp {
  name: string;
  icon: LucideIcon;
  /** Tailwind classes for the icon tile — the same hues the portal uses for each app. */
  tone: string;
  summary: string;
  points: string[];
}

/**
 * What the suite is, in the words a haulier would use. Kept to what is BUILT:
 * Google's reviewers compare the home page against what the app actually does,
 * and a customer who signs up for a promised feature notices too.
 */
const APPS: SuiteApp[] = [
  {
    name: 'myTMS',
    icon: Truck,
    tone: 'bg-blue-50 text-blue-700 ring-blue-100',
    summary: 'Transport management, from the first call to the final invoice.',
    points: [
      'Orders, templates and repeat jobs',
      'Planning and driver assignment',
      'Ferry routes and bookings',
      'Rates, charges and invoicing',
    ],
  },
  {
    name: 'myDepot',
    icon: Warehouse,
    tone: 'bg-amber-50 text-amber-700 ring-amber-100',
    summary: 'Bookings, storage and charging for your depots.',
    points: ['Depot bookings and storage', 'Product approvals', 'Depot charges and invoicing'],
  },
  {
    name: 'myCheck',
    icon: ShieldCheck,
    tone: 'bg-violet-50 text-violet-700 ring-violet-100',
    summary: 'Fleet compliance without the paperwork.',
    points: ['Walk-around checks', 'Defects with photos', 'Roadside-ready records'],
  },
  {
    name: 'mySubsistence',
    icon: Wallet,
    tone: 'bg-cyan-50 text-cyan-700 ring-cyan-100',
    summary: 'Driver subsistence, worked out from where the truck actually went.',
    points: ['Claims built from tachograph and GPS data', 'Your own allowance rates', 'Reviewed by the office'],
  },
  {
    name: 'myDriver',
    icon: Smartphone,
    tone: 'bg-indigo-50 text-indigo-700 ring-indigo-100',
    summary: 'The driver’s app — jobs, checks and proof of delivery on the phone.',
    points: ['Sign in with a phone number', 'Checks, photos and signatures', 'Messages from the office'],
  },
];

interface Strength {
  icon: LucideIcon;
  title: string;
  body: string;
}

const STRENGTHS: Strength[] = [
  {
    icon: Network,
    title: 'One directory behind every app',
    body: 'Customers, suppliers, sites, people and vehicles are entered once and used everywhere — no copying between systems.',
  },
  {
    icon: KeyRound,
    title: 'One login, the right access',
    body: 'Each person sees the apps and screens their role allows, across every organisation in your account.',
  },
  {
    icon: History,
    title: 'A full audit trail',
    body: 'Who changed what, and when — on orders, customers and organisation settings.',
  },
  {
    icon: FileText,
    title: 'Documents in your own style',
    body: 'Invoices, credit notes and proformas laid out the way you want them, per customer if needed.',
  },
  {
    icon: Leaf,
    title: 'CO₂ on every job',
    body: 'Route-based emission estimates calculated per order, ready for the customers who ask.',
  },
  {
    icon: Mail,
    title: 'Sent from your address',
    body: 'Connect your Microsoft 365 or Google Workspace mailbox and documents go to customers from your own email address.',
  },
];

/**
 * The public front door at my.stratum.ie for anybody not signed in. Signed-in
 * staff never see it — `/` is still the portal for them.
 */
export function LandingPage() {
  return (
    <PublicLayout>
      <Hero />
      <Suite />
      <Strengths />
      <YourData />
      <ClosingCall />
    </PublicLayout>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden bg-slate-950 text-white">
      {/* The three dot colours of the Stratum mark, blurred into the backdrop. */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute -left-24 top-10 h-72 w-72 rounded-full bg-[#009ddf]/25 blur-3xl" />
        <div className="absolute right-0 top-40 h-64 w-64 rounded-full bg-[#e40420]/15 blur-3xl" />
        <div className="absolute bottom-0 left-1/2 h-56 w-56 rounded-full bg-[#fdc300]/10 blur-3xl" />
      </div>

      <div className="relative mx-auto grid max-w-6xl gap-12 px-4 py-20 sm:px-6 lg:grid-cols-[1.15fr_1fr] lg:items-center lg:py-28">
        <div>
          <p className="mb-4 inline-flex items-center gap-2 rounded-full bg-white/5 px-3 py-1 text-xs font-medium text-sky-200 ring-1 ring-white/10">
            <span className="h-1.5 w-1.5 rounded-full bg-[#009ddf]" />
            The platform for transport and logistics
          </p>
          <h1 className="text-4xl font-semibold leading-tight tracking-tight sm:text-5xl">
            Run the whole operation from{' '}
            <span className="bg-gradient-to-r from-[#33b6ec] to-[#fdc300] bg-clip-text text-transparent">
              one place
            </span>
            .
          </h1>
          <p className="mt-6 max-w-xl text-lg leading-relaxed text-slate-300">
            {COMPANY.product} brings orders, planning, invoicing, depots, safety checks and the
            driver’s phone together — one platform, one login, built for hauliers in Ireland and
            the UK.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <SignInButton variant="hero" />
            <a href="#apps" className="text-sm font-medium text-slate-300 hover:text-white">
              See what’s inside →
            </a>
          </div>
        </div>

        <HeroPanel />
      </div>
    </section>
  );
}

/** A stylised glance at a day's board — decoration, so hidden from screen readers. */
function HeroPanel() {
  const rows = [
    { ref: 'ORD-10482', route: 'Dublin → Holyhead', status: 'On route', dot: 'bg-[#009ddf]' },
    { ref: 'ORD-10483', route: 'Cork → Rosslare', status: 'Planned', dot: 'bg-[#fdc300]' },
    { ref: 'ORD-10486', route: 'Belfast → Cairnryan', status: 'Delivered', dot: 'bg-emerald-400' },
    { ref: 'ORD-10487', route: 'Galway → Dublin Port', status: 'Invoiced', dot: 'bg-slate-400' },
  ];
  return (
    <div aria-hidden className="relative">
      <div className="rounded-2xl bg-white/5 p-5 shadow-2xl ring-1 ring-white/10 backdrop-blur">
        <div className="mb-4 flex items-center justify-between">
          <span className="text-sm font-medium text-slate-200">Today’s jobs</span>
          <span className="rounded-full bg-emerald-400/10 px-2 py-0.5 text-[11px] font-medium text-emerald-300">
            4 active
          </span>
        </div>
        <ul className="space-y-2">
          {rows.map((r) => (
            <li
              key={r.ref}
              className="flex items-center justify-between rounded-lg bg-slate-900/70 px-3.5 py-3 ring-1 ring-white/5"
            >
              <div>
                <p className="font-mono text-xs text-slate-400">{r.ref}</p>
                <p className="text-sm text-slate-100">{r.route}</p>
              </div>
              <span className="flex items-center gap-2 text-xs text-slate-300">
                <span className={`h-2 w-2 rounded-full ${r.dot}`} />
                {r.status}
              </span>
            </li>
          ))}
        </ul>
        <div className="mt-4 grid grid-cols-3 gap-2 text-center">
          {[
            ['12', 'drivers out'],
            ['3', 'ferries booked'],
            ['€18.4k', 'to invoice'],
          ].map(([value, label]) => (
            <div key={label} className="rounded-lg bg-slate-900/70 px-2 py-3 ring-1 ring-white/5">
              <p className="text-base font-semibold text-white">{value}</p>
              <p className="text-[11px] text-slate-400">{label}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Suite() {
  return (
    <section id="apps" className="scroll-mt-16 bg-white py-20">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <SectionHeading
          eyebrow="The suite"
          title="An app for every part of the operation"
          body="Subscribe to the apps you need. They share one directory, so a customer added in myTMS is already there in myDepot."
        />
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {APPS.map((app) => (
            <article
              key={app.name}
              className="flex flex-col rounded-xl border border-slate-200 bg-white p-6 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
            >
              <div className={`mb-4 flex h-11 w-11 items-center justify-center rounded-lg ring-1 ${app.tone}`}>
                <app.icon className="h-5 w-5" aria-hidden />
              </div>
              <h3 className="text-lg font-semibold text-slate-900">{app.name}</h3>
              <p className="mt-1 text-sm text-slate-600">{app.summary}</p>
              <ul className="mt-4 space-y-1.5 text-sm text-slate-700">
                {app.points.map((p) => (
                  <li key={p} className="flex gap-2">
                    <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-slate-400" />
                    {p}
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function Strengths() {
  return (
    <section className="border-y border-slate-200 bg-slate-50 py-20">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <SectionHeading
          eyebrow="Why one platform"
          title="Enter it once. Use it everywhere."
          body="The things that make separate systems painful are the things a single platform gets for free."
        />
        <div className="mt-12 grid gap-x-10 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">
          {STRENGTHS.map((s) => (
            <div key={s.title} className="flex gap-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white text-[#0089c3] shadow-sm ring-1 ring-slate-200">
                <s.icon className="h-5 w-5" aria-hidden />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900">{s.title}</h3>
                <p className="mt-1 text-sm leading-relaxed text-slate-600">{s.body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/**
 * Spells out what connecting a mailbox does and does not allow. Google's
 * reviewers look for exactly this on the home page before approving the
 * Gmail send permission; a customer's IT admin looks for it before approving
 * the Microsoft one.
 */
function YourData() {
  return (
    <section className="bg-white py-20">
      <div className="mx-auto grid max-w-6xl gap-10 px-4 sm:px-6 lg:grid-cols-2 lg:items-center">
        <div>
          <SectionHeading
            align="left"
            eyebrow="Your data"
            title="Connected email that can send — and nothing else"
            body={`When you connect a Microsoft 365 or Google Workspace mailbox, ${COMPANY.product} asks to send email as that address — plus your name and email address, to show which mailbox is connected. It never reads, searches or deletes your mail.`}
          />
          <p className="mt-6 text-sm text-slate-600">
            Read exactly what we collect and why in our{' '}
            <Link to="/privacy" className="font-medium text-[#0089c3] hover:underline">
              privacy policy
            </Link>
            .
          </p>
        </div>
        <ul className="grid gap-3">
          {[
            ['Send only', 'Documents you choose to send — invoices, quotes, confirmations — go out from your address, and appear in its Sent folder.'],
            ['No inbox access', 'We do not read, store or analyse the messages in your mailbox.'],
            ['Encrypted at rest', 'The access token is encrypted with a key held outside the database.'],
            ['Yours to revoke', 'Disconnect in My Stratum, or remove the app from your Microsoft or Google account, at any time.'],
          ].map(([title, body]) => (
            <li key={title} className="flex gap-3 rounded-xl border border-slate-200 p-4">
              <Lock className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" aria-hidden />
              <div>
                <p className="text-sm font-semibold text-slate-900">{title}</p>
                <p className="mt-0.5 text-sm text-slate-600">{body}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function ClosingCall() {
  return (
    <section className="bg-slate-950 py-16 text-white">
      <div className="mx-auto flex max-w-6xl flex-col items-start justify-between gap-6 px-4 sm:px-6 md:flex-row md:items-center">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">Already a customer?</h2>
          <p className="mt-2 text-slate-300">
            Sign in to manage your account.{' '}
            {isPlaceholder(COMPANY.salesEmail) ? (
              <>
                New to {COMPANY.product}?{' '}
                <a href={COMPANY.website} className="text-sky-300 hover:underline">
                  Get in touch through stratum.ie
                </a>
                .
              </>
            ) : (
              <>
                New to {COMPANY.product}?{' '}
                <a href={`mailto:${COMPANY.salesEmail}`} className="text-sky-300 hover:underline">
                  Talk to us
                </a>
                .
              </>
            )}
          </p>
        </div>
        <SignInButton variant="hero" />
      </div>
    </section>
  );
}

function SectionHeading({
  eyebrow,
  title,
  body,
  align = 'center',
}: {
  eyebrow: string;
  title: string;
  body: string;
  align?: 'center' | 'left';
}) {
  return (
    <div className={align === 'center' ? 'mx-auto max-w-2xl text-center' : 'max-w-xl'}>
      <p className="text-xs font-semibold uppercase tracking-wider text-[#0089c3]">{eyebrow}</p>
      <h2 className="mt-2 text-3xl font-semibold tracking-tight text-slate-900">{title}</h2>
      <p className="mt-4 text-base leading-relaxed text-slate-600">{body}</p>
    </div>
  );
}
